import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Camera, Trophy, User, Upload, Edit3, Image, Plus } from "lucide-react";
import { useLocation } from "wouter";
import { isUnauthorizedError } from "@/lib/authUtils";
import { useToast } from "@/hooks/use-toast";
import { ObjectUploader } from "@/components/ObjectUploader";
import { apiRequest } from "@/lib/queryClient";

export default function Profile() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [profilePicDialogOpen, setProfilePicDialogOpen] = useState(false);
  const [artUploadDialogOpen, setArtUploadDialogOpen] = useState(false);
  const [artTitle, setArtTitle] = useState("");
  const [artDescription, setArtDescription] = useState("");
  const [artType, setArtType] = useState("graffiti");

  // Fetch user discoveries
  const { data: discoveries, isLoading: discoveryLoading } = useQuery({
    queryKey: ["/api/discoveries/user"],
    enabled: !!user,
  });

  // Fetch user achievements
  const { data: achievements, isLoading: achievementsLoading } = useQuery({
    queryKey: ["/api/profile/achievements"],
    enabled: !!user,
  });

  // Fetch user art gallery
  const { data: userArt, isLoading: artLoading } = useQuery({
    queryKey: ["/api/profile/art"],
    enabled: !!user,
  });

  // Profile picture update mutation
  const updateProfilePicMutation = useMutation({
    mutationFn: async (imageUrl: string) => {
      await apiRequest("PUT", "/api/profile/picture", { imageUrl });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title: "Success",
        description: "Profile picture updated successfully!",
      });
      setProfilePicDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error updating profile picture:", error);
      toast({
        title: "Error",
        description: "Failed to update profile picture",
        variant: "destructive",
      });
    },
  });

  // Upload art mutation
  const uploadArtMutation = useMutation({
    mutationFn: async (artData: any) => {
      await apiRequest("POST", "/api/profile/art", artData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile/art"] });
      toast({
        title: "Artwork Added!",
        description: "Your artwork has been added to your gallery.",
      });
      setArtUploadDialogOpen(false);
      setArtTitle("");
      setArtDescription("");
      setArtType("graffiti");
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Upload Failed",
        description: "Failed to add artwork. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-base">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative bg-gradient-to-b from-dark-base to-dark-elevated text-white">
      <header className="relative z-10 p-6 flex items-center justify-between">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/")}
            className="text-neon-green mr-4"
            data-testid="button-back-home"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold" data-testid="heading-profile">Profile</h1>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="text-gray-400 border-gray-400 hover:bg-gray-400/20"
          onClick={() => window.location.href = '/api/logout'}
          data-testid="button-signout-profile"
        >
          Sign Out
        </Button>
      </header>
      
      <main className="relative z-10 flex-1 px-6 pb-8">
        {/* Profile Header */}
        <Card className="bg-dark-elevated border-gray-700 mb-6">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className="relative w-20 h-20 mx-auto mb-4">
                {user?.profileImageUrl ? (
                  <img 
                    src={user.profileImageUrl} 
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-2 border-neon-green"
                    data-testid="profile-picture"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-br from-neon-green to-teal-custom rounded-full flex items-center justify-center">
                    <User className="text-2xl text-dark-base" size={32} />
                  </div>
                )}
                <Dialog open={profilePicDialogOpen} onOpenChange={setProfilePicDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-neon-green hover:bg-neon-green/80 text-dark-base"
                      data-testid="button-edit-profile-pic"
                    >
                      <Edit3 size={14} />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-dark-elevated border-gray-700 text-white">
                    <DialogHeader>
                      <DialogTitle>Update Profile Picture</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <ObjectUploader
                        maxNumberOfFiles={1}
                        maxFileSize={5242880} // 5MB
                        onGetUploadParameters={async () => {
                          const response = await apiRequest("POST", "/api/objects/upload");
                          const data = await response.json();
                          return {
                            method: "PUT" as const,
                            url: data.uploadURL,
                          };
                        }}
                        onComplete={(result) => {
                          if (result.successful && result.successful.length > 0) {
                            const uploadUrl = result.successful[0].uploadURL;
                            if (uploadUrl) {
                              updateProfilePicMutation.mutate(uploadUrl);
                            }
                          } else if (result.failed && result.failed.length > 0) {
                            toast({
                              title: "Upload Failed",
                              description: "Failed to upload profile picture. Please try again.",
                              variant: "destructive",
                            });
                          }
                        }}
                        buttonClassName="w-full"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Upload size={16} />
                          <span>Choose Profile Picture</span>
                        </div>
                      </ObjectUploader>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <h2 className="text-xl font-bold" data-testid="text-profile-name">{user?.username || 'Street Artist'}</h2>
              <p className="text-gray-400" data-testid="text-join-date">
                Joined {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Recently'}
              </p>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-neon-green" data-testid="stat-discoveries">{user?.discoveries || 0}</div>
                <div className="text-xs text-gray-400">Discoveries</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-coral" data-testid="stat-level">{user?.level || 1}</div>
                <div className="text-xs text-gray-400">Level</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400" data-testid="stat-tokens">{user?.tokens || 0}</div>
                <div className="text-xs text-gray-400">Tokens</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Recent Discoveries */}
        <Card className="bg-dark-elevated border-gray-700 mb-6">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Camera className="text-neon-green mr-2" size={20} />
              Recent Discoveries
            </h3>
            
            {discoveryLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin w-6 h-6 border-4 border-neon-green border-t-transparent rounded-full" />
              </div>
            ) : discoveries && Array.isArray(discoveries) && discoveries.length > 0 ? (
              <div className="space-y-3" data-testid="list-discoveries">
                {discoveries.slice(0, 5).map((discovery: any, index: number) => (
                  <div key={discovery.id} className="flex items-center bg-dark-surface rounded-lg p-3" data-testid={`discovery-item-${index}`}>
                    {discovery.imageUrl ? (
                      <img 
                        src={discovery.imageUrl} 
                        alt={discovery.name} 
                        className="w-12 h-12 rounded-lg object-cover mr-3"
                        data-testid={`discovery-image-${index}`}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                        <Camera size={20} className="text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-medium" data-testid={`discovery-name-${index}`}>{discovery.name}</div>
                      <div className="text-sm text-gray-400" data-testid={`discovery-time-${index}`}>
                        {new Date(discovery.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-neon-green text-sm" data-testid={`discovery-xp-${index}`}>+15 XP</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400" data-testid="empty-discoveries">
                <Camera size={48} className="mx-auto mb-4 opacity-50" />
                <p>No discoveries yet</p>
                <p className="text-sm">Start exploring to find street art!</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Personal Art Gallery */}
        <Card className="bg-dark-elevated border-gray-700 mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Image className="text-coral mr-2" size={20} />
                My Art Gallery
              </h3>
              <Dialog open={artUploadDialogOpen} onOpenChange={setArtUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    size="sm" 
                    className="bg-coral hover:bg-coral/80 text-white"
                    data-testid="button-add-art"
                  >
                    <Plus size={16} className="mr-1" />
                    Add Art
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-dark-elevated border-gray-700 text-white">
                  <DialogHeader>
                    <DialogTitle>Add Artwork to Gallery</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="art-title">Title</Label>
                      <Input
                        id="art-title"
                        value={artTitle}
                        onChange={(e) => setArtTitle(e.target.value)}
                        placeholder="Enter artwork title"
                        className="bg-dark-surface border-gray-600 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="art-description">Description</Label>
                      <Textarea
                        id="art-description"
                        value={artDescription}
                        onChange={(e) => setArtDescription(e.target.value)}
                        placeholder="Describe your artwork"
                        className="bg-dark-surface border-gray-600 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="art-type">Art Type</Label>
                      <select
                        id="art-type"
                        value={artType}
                        onChange={(e) => setArtType(e.target.value)}
                        className="w-full p-2 bg-dark-surface border border-gray-600 rounded-md text-white"
                      >
                        <option value="graffiti">Graffiti</option>
                        <option value="digital">Digital Art</option>
                        <option value="photo">Photography</option>
                        <option value="sketch">Sketch</option>
                      </select>
                    </div>
                    <ObjectUploader
                      maxNumberOfFiles={1}
                      maxFileSize={10485760} // 10MB
                      onGetUploadParameters={async () => {
                        const response = await apiRequest("POST", "/api/objects/upload");
                        const data = await response.json();
                        return {
                          method: "PUT" as const,
                          url: data.uploadURL,
                        };
                      }}
                      onComplete={(result) => {
                        if (result.successful && result.successful.length > 0 && artTitle.trim()) {
                          const uploadUrl = result.successful[0].uploadURL;
                          if (uploadUrl) {
                            uploadArtMutation.mutate({
                              imageUrl: uploadUrl,
                              title: artTitle.trim(),
                              description: artDescription.trim(),
                              artType: artType,
                            });
                          }
                        } else if (result.failed && result.failed.length > 0) {
                          toast({
                            title: "Upload Failed",
                            description: "Failed to upload image. Please try again.",
                            variant: "destructive",
                          });
                        }
                      }}
                      buttonClassName="w-full"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Upload size={16} />
                        <span>Upload Artwork</span>
                      </div>
                    </ObjectUploader>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            {artLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin w-6 h-6 border-4 border-coral border-t-transparent rounded-full" />
              </div>
            ) : userArt && Array.isArray(userArt) && userArt.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="art-gallery">
                {userArt.map((art: any, index: number) => (
                  <div key={art.id} className="bg-dark-surface rounded-lg overflow-hidden" data-testid={`art-item-${index}`}>
                    <img 
                      src={art.imageUrl} 
                      alt={art.title}
                      className="w-full h-32 object-cover"
                      data-testid={`art-image-${index}`}
                    />
                    <div className="p-3">
                      <h4 className="font-medium text-sm mb-1" data-testid={`art-title-${index}`}>{art.title}</h4>
                      <p className="text-xs text-gray-400 mb-2">{art.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs bg-coral/20 text-coral px-2 py-1 rounded">{art.artType}</span>
                        <span className="text-xs text-gray-500">{art.views || 0} views</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400" data-testid="no-art">
                <Image size={48} className="mx-auto mb-4 opacity-50" />
                <p>No artwork in your gallery yet. Add your first piece!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Achievement Badges */}
        <Card className="bg-dark-elevated border-gray-700">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Trophy className="text-yellow-400 mr-2" size={20} />
              Achievements
            </h3>
            
            <div className="grid grid-cols-3 gap-3" data-testid="achievements-grid">
              {/* Default starter achievements */}
              <div className={`text-center bg-dark-surface rounded-lg p-3 ${user?.discoveries && user.discoveries > 0 ? '' : 'opacity-50'}`} data-testid="achievement-first-discovery">
                <div className="text-2xl mb-2">👁️</div>
                <div className="text-xs font-medium">First Discovery</div>
              </div>
              <div className={`text-center bg-dark-surface rounded-lg p-3 opacity-50`} data-testid="achievement-pin-dropper">
                <div className="text-2xl mb-2">📍</div>
                <div className="text-xs font-medium">Pin Dropper</div>
              </div>
              <div className={`text-center bg-dark-surface rounded-lg p-3 opacity-50`} data-testid="achievement-social-explorer">
                <div className="text-2xl mb-2">👥</div>
                <div className="text-xs font-medium">Social Explorer</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
